import React from 'react';
import { History, UserPlus, FileText, Flag, CalendarClock, BadgeDollarSign, MessageSquare, Pencil, Eye } from 'lucide-react';
import { Card, EmptyState, ACTION_LABELS, fmtDateTime, fmtDate } from './drawerShared';

const ICONS = {
  create: UserPlus, update: Pencil, stage_change: Flag,
  interview_note_added: MessageSquare, interview_scheduled: CalendarClock, interview_updated: CalendarClock,
  offer_created: BadgeDollarSign, offer_reissued: BadgeDollarSign, offer_status_changed: BadgeDollarSign,
  resume_uploaded: FileText, resume_replaced: FileText, resume_viewed: Eye,
  converted_to_employee: UserPlus, candidate_converted_to_employee: UserPlus,
};
const TONES = {
  stage_change: 'bg-blue-600', create: 'bg-slate-500', resume_viewed: 'bg-slate-300',
  offer_created: 'bg-amber-500', offer_reissued: 'bg-amber-500', offer_status_changed: 'bg-amber-500',
  converted_to_employee: 'bg-emerald-600', candidate_converted_to_employee: 'bg-emerald-600',
};

const changeLines = (entry) => (entry.changes || [])
  .filter((c) => c.old_value !== null || typeof c.new_value === 'string')
  .slice(0, 4)
  .map((c) => (c.field === 'candidate' ? String(c.new_value) : c.old_value != null ? `${c.field}: ${c.old_value} → ${c.new_value}` : `${c.field}: ${c.new_value}`));

const dayKey = (d) => { try { return fmtDate(d); } catch { return String(d).slice(0, 10); } };

export const ActivityTab = ({ auditLog }) => {
  const groups = auditLog.reduce((acc, e) => { const k = dayKey(e.changed_at); (acc[k] = acc[k] || []).push(e); return acc; }, {});
  const days = Object.keys(groups);
  return (
    <div data-testid="tab-panel-activity">
      <Card title={`Activity History (${auditLog.length})`} icon={History} data-testid="drawer-activity-section" bodyClassName="py-5">
        {auditLog.length === 0 ? (
          <EmptyState icon={History} title="No activity recorded" hint="Profile updates, stage changes, interviews, offers and resume events will appear here." testId="activity-empty" />
        ) : (
          <div className="space-y-6">
            {days.map((day) => (
              <div key={day} data-testid="activity-day-group">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">{day}</p>
                <ol className="relative ml-3 border-l-2 border-slate-100 pl-6">
                  {groups[day].map((entry, idx) => {
                    const Icon = ICONS[entry.action] || History;
                    const lines = changeLines(entry);
                    return (
                      <li key={idx} className="relative pb-5 last:pb-0" data-testid="activity-item">
                        <span className={`absolute -left-[31px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white ${TONES[entry.action] || 'bg-slate-400'}`}>
                          <Icon className="h-3 w-3 text-white" />
                        </span>
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                          <p className="text-sm font-semibold text-slate-800" data-testid="activity-action">{ACTION_LABELS[entry.action] || entry.action}</p>
                          <p className="text-[11px] text-slate-400">{fmtDateTime(entry.changed_at)}</p>
                        </div>
                        {lines.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {lines.map((l, i) => <li key={i} className="break-words font-mono text-[11px] leading-relaxed text-slate-500">{l}</li>)}
                          </ul>
                        )}
                        <p className="mt-1 text-[11px] text-slate-400">by <span className="font-medium text-slate-600">{entry.changed_by_name || 'System'}</span></p>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
